import { useEffect } from "react";
import { apiFetch, ApiError } from "../lib/api.ts";

/**
 * Warm the per-tenant `core` container as soon as the authenticated shell mounts, so its (Durable
 * Object container) cold start overlaps the SPA render instead of starting only when the first
 * screen's query runs. Every `/api/v1/*` call except the stream forwards to a container that sleeps
 * after idle (`CoreContainer.sleepAfter`), so the first call after a quiet period pays the boot; a
 * fire-and-forget ping here moves that boot earlier and in parallel.
 *
 * Deliberately minimal and side-effect-free beyond the wake: ONE GET to a cheap container route,
 * run once per shell mount, with errors swallowed (a `waking` 503 is the expected first answer and
 * the normal cold-core UX — the waking banner and the screens' own retries — takes it from there).
 * It never blocks render and never surfaces anything to the operator.
 */
export function useWarmCore(): void {
  useEffect(() => {
    const controller = new AbortController();
    // `/api/v1/overview` is the lightest container-backed read and is what the default route needs
    // anyway; waking it here means the Overview query often finds the container already up.
    void apiFetch<unknown>("/overview", { signal: controller.signal }).catch((error: unknown) => {
      // Expected: `waking` while the container boots, or `abort` on unmount. Nothing to do.
      void (error instanceof ApiError);
    });
    return () => controller.abort();
  }, []);
}
